import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { post } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { ConfirmDialog } from '../components/ConfirmDialog';

interface AccessRule {
  ip: string;
  name: string;
  welcome: string;
}

interface AccessConfig {
  [ip: string]: { name: string; welcome: string };
}

export function AccessPage() {
  const [rules,    setRules]    = useState<AccessRule[]>([]);
  const [loading,  setLoading]  = useState(false);

  // Add form state
  const [showAdd,  setShowAdd]  = useState(false);
  const [addIp,    setAddIp]    = useState('');
  const [addName,  setAddName]  = useState('');
  const [addWelcome, setAddWelcome] = useState('');
  const [adding,   setAdding]   = useState(false);

  // Confirm dialog
  const [confirm,  setConfirm]  = useState<{ ip: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const parseConfig = (cfg: AccessConfig) =>
    Object.entries(cfg).map(([ip, v]) => ({ ip, name: v.name, welcome: v.welcome }));

  const fetchAccess = async () => {
    setLoading(true);
    try {
      const data = await post<{ access_config: AccessConfig }>('/panel/access', { action: 'list' });
      setRules(parseConfig(data.access_config || {}));
    } catch (err: unknown) {
      toast.error('Failed to load access list: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAccess(); }, []);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addIp.trim() || !addName.trim()) {
      toast.error('IP address and display name are required.');
      return;
    }
    setAdding(true);
    const id = toast.loading(`Adding access for ${addIp}...`);
    try {
      const data = await post<{ access_config: AccessConfig }>('/panel/access', {
        action:  'add',
        ip:      addIp.trim(),
        name:    addName.trim(),
        welcome: addWelcome.trim(),
      });
      setRules(parseConfig(data.access_config || {}));
      toast.success(`Access granted to ${addIp}`, { id });
      setAddIp(''); setAddName(''); setAddWelcome('');
      setShowAdd(false);
    } catch (err: unknown) {
      toast.error('Failed: ' + (err instanceof Error ? err.message : 'Unknown error'), { id });
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm) return;
    setDeleting(true);
    const id = toast.loading(`Revoking access for ${confirm.ip}...`);
    try {
      const data = await post<{ access_config: AccessConfig }>('/panel/access', {
        action: 'delete',
        ip:     confirm.ip,
      });
      setRules(parseConfig(data.access_config || {}));
      toast.success(`Access revoked for ${confirm.ip}`, { id });
    } catch (err: unknown) {
      toast.error('Failed: ' + (err instanceof Error ? err.message : 'Unknown error'), { id });
    } finally {
      setDeleting(false);
      setConfirm(null);
    }
  };

  return (
    <>
      {confirm && (
        <ConfirmDialog
          title="Revoke Panel Access"
          message={
            <>
              Remove access for <span className="font-bold text-white">{confirm.name}</span> (
              <code className="font-mono text-cyberCyan">{confirm.ip}</code>)? They will no longer be able to reach the owner panel.
            </>
          }
          confirmLabel="Revoke Access"
          confirmVariant="red"
          onConfirm={handleDelete}
          onCancel={() => !deleting && setConfirm(null)}
        />
      )}

      <div className="flex flex-col gap-6 animate-fadeIn">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-[9px] font-mono tracking-[0.2em] text-slate-600 uppercase mb-1">BackRoom Management</div>
            <h1 className="text-2xl font-black font-display uppercase tracking-tight text-white">
              Access <span className="bg-gradient-to-r from-cyberCyan to-blue-400 bg-clip-text text-transparent">Control</span>
            </h1>
            <p className="text-xs text-slate-500 mt-1">IP-based allow list for the backrooms owner access.</p>
          </div>
          <Button variant="cyan" size="sm" onClick={() => setShowAdd(!showAdd)}>
            <i className="fa-solid fa-shield-plus" />
            Add IP Rule
          </Button>
        </div>

        {/* Add form */}
        {showAdd && (
          <Card variant="glass" className="p-6 max-w-lg">
            <form onSubmit={handleAdd} className="flex flex-col gap-4">
              <h3 className="text-xs font-bold font-display uppercase tracking-wider text-white mb-1">
                Grant Panel Access
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[9px] font-extrabold tracking-wider uppercase text-slate-400">IP Address</label>
                  <input
                    type="text"
                    value={addIp}
                    onChange={e => setAddIp(e.target.value)}
                    placeholder="e.g. 96.126.188.46"
                    className="input-base"
                    disabled={adding}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[9px] font-extrabold tracking-wider uppercase text-slate-400">Display Name</label>
                  <input
                    type="text"
                    value={addName}
                    onChange={e => setAddName(e.target.value)}
                    placeholder="e.g. Joker (Admin)"
                    className="input-base"
                    disabled={adding}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[9px] font-extrabold tracking-wider uppercase text-slate-400">
                  Welcome Message <span className="text-slate-600 normal-case">(optional)</span>
                </label>
                <input
                  type="text"
                  value={addWelcome}
                  onChange={e => setAddWelcome(e.target.value)}
                  placeholder="Shown to user on panel load..."
                  className="input-base"
                  disabled={adding}
                />
              </div>

              <div className="flex justify-end gap-3 mt-1">
                <Button type="button" variant="ghost" size="sm" onClick={() => setShowAdd(false)}>Cancel</Button>
                <Button type="submit" variant="cyan" size="sm" loading={adding}>
                  <i className="fa-solid fa-plus" /> Add Rule
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Rules table */}
        <Card variant="glass" className="overflow-hidden relative">
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyberCyan/10 to-transparent" />

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs font-mono">
              <thead className="bg-white/[0.02] text-slate-400 uppercase tracking-wider border-b border-white/[0.04]">
                <tr>
                  <th className="px-5 py-3.5">IP Address</th>
                  <th className="px-5 py-3.5">Display Name</th>
                  <th className="px-5 py-3.5">Welcome Message</th>
                  <th className="px-5 py-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04] text-slate-200">
                {loading && rules.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-5 py-8 text-center text-cyberCyan">
                      <i className="fa-solid fa-spinner animate-spin mr-2" />Loading rules...
                    </td>
                  </tr>
                )}
                {!loading && rules.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-5 py-8 text-center text-slate-500">
                      No access rules configured.
                    </td>
                  </tr>
                )}
                {rules.map(rule => (
                  <tr key={rule.ip} className="hover:bg-white/[0.01] transition-colors">
                    <td className="px-5 py-4">
                      <code className="text-cyberCyan font-bold">{rule.ip}</code>
                    </td>
                    <td className="px-5 py-4 text-slate-200 font-semibold">{rule.name}</td>
                    <td className="px-5 py-4 text-slate-500 truncate max-w-[240px]">
                      {rule.welcome || <span className="text-slate-700 italic">default</span>}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        onClick={() => setConfirm({ ip: rule.ip, name: rule.name })}
                        className="text-slate-500 hover:text-red-400 transition-colors p-1 rounded"
                        title="Revoke access"
                        aria-label={`Revoke access for ${rule.name}`}
                      >
                        <i className="fa-solid fa-shield-xmark" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </>
  );
}
